import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);

        // Amène la cliente sur le visuel du coloris choisi, où qu'elle en soit dans
        // le défilement. L'ordre des photos, lui, ne bouge pas.
        requestAnimationFrame(() => revealSelectedMedia(newMediaGallery));
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

const SELECTED_MEDIA_CLASS = 'product-media-container--variant-selected';

/**
 * Amène la cliente sur le visuel du coloris sélectionné.
 *
 * Les photos restent dans l'ordre de la fiche produit (ambiances puis coloris).
 * Le visuel de la variante choisie est marqué côté Liquid : on le retrouve ici
 * pour caler le carrousel dessus sur mobile, et pour y faire défiler la page en
 * grille sur ordinateur.
 *
 * @param {Element} gallery - La galerie fraîchement insérée dans la page.
 */
function revealSelectedMedia(gallery) {
  if (!(gallery instanceof HTMLElement) || !gallery.isConnected) return;

  // Carrousel (mobile, ou présentation carrousel) : on se cale sur la diapositive.
  const slideshow = /** @type {any} */ (gallery).slideshow;
  const slides = Array.from(gallery.querySelectorAll('slideshow-slide'));
  const slideIndex = slides.findIndex(
    (slide) => slide.classList.contains(SELECTED_MEDIA_CLASS) || slide.querySelector(`.${SELECTED_MEDIA_CLASS}`)
  );

  if (slideshow && typeof slideshow.select === 'function' && slideIndex > -1) {
    try {
      slideshow.select(slideIndex, undefined, { animate: false });
    } catch (error) {
      console.warn('[media-gallery] Impossible de se caler sur la diapositive :', error);
    }
  }

  // Grille (ordinateur) : on fait défiler la page jusqu'à la photo du coloris.
  const gridTarget = gallery.querySelector(`.media-gallery__grid .${SELECTED_MEDIA_CLASS}`);
  const slideTarget = slideIndex > -1 ? slides[slideIndex] : null;

  // On ne garde que l'élément réellement affiché : la grille et le carrousel
  // coexistent dans le balisage, l'un des deux est masqué selon la largeur d'écran.
  const target = [gridTarget, slideTarget].find(
    (candidate) => candidate instanceof HTMLElement && candidate.offsetParent !== null
  );

  if (!(target instanceof HTMLElement)) return;

  // Si la photo est déjà sous les yeux de la cliente, on ne bouge pas la page.
  const bounds = target.getBoundingClientRect();
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
  if (bounds.top >= 0 && bounds.bottom <= viewportHeight) return;

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  target.scrollIntoView({
    behavior: prefersReducedMotion ? 'auto' : 'smooth',
    block: 'center',
    inline: 'nearest',
  });
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
